/**
 * AppTabsHost — any app page as a browser-like tab, in its own browser tab or
 * desktop window.
 *
 * Rendered by App for /popout/app (web) and index.html?popout=app (desktop),
 * with no sidebar shell. Which tabs are open, and which is in front, live in
 * the URL (history.replaceState), so a reload — or a desktop window restored by
 * the OS — comes back the same.
 *
 * EACH TAB IS AN IFRAME of the chromeless page pop-out (/popout/page?tab=…).
 * The app is one giant component keyed on a single `activeTab` with
 * window-level listeners (storage, changeTab, popstate, keyboard shortcuts);
 * two pages rendered side by side in one document would fight over all of it.
 * A same-origin iframe gives every tab its own copy of that state for free,
 * while sharing the sign-in and active org through localStorage. A tab is
 * mounted the first time it is shown and then kept mounted (hidden), so a
 * half-typed reply or a scroll position survives switching tabs.
 *
 * A page that navigates (setActiveTab) stays inside its tab; it tells the host
 * over postMessage (origin- and source-checked, shape-validated) so the tab's
 * title and the URL follow.
 *
 * Keys: Ctrl/Cmd+1…8 jump to a tab, Ctrl/Cmd+9 to the last one. On the desktop
 * app Ctrl/Cmd+W closes the current tab (and the window with the last tab); in
 * a browser Cmd+W stays the browser's. Ctrl/Cmd+Shift+PageUp/PageDown or
 * Alt+Shift+←/→ move the current tab (lib/tabStrip.ts). Pages forward these
 * keys when they have focus, since a key pressed inside an iframe never
 * reaches this document.
 *
 * The strip itself (reorder, pin, move to another window, drag a tab out) is
 * the shared TabStrip; pinned tabs are the URL's `pinned=` count.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppWindow, Plus } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  ADD_TABS_EVENT,
  APP_POPOUT_PATH,
  HOST_CHANNEL,
  LAST_DESKTOP_HOST_KEY,
  MAX_POPOUT_TABS,
  appTabKey,
  buildAppPopoutQuery,
  buildPagePopoutUrl,
  openAppPopout,
  parseAppPopoutParams,
  parseAppTabKey,
  registerInAppTabHandler,
  stateFromKeys,
  type AddTabsEvent,
  type AppPopoutState,
  type AppTabSpec,
  type OpenResult,
} from '@/lib/popout';
import { currentDesktopWindowLabel } from '@/lib/desktopWindow';
import type { MoveTarget } from '@/lib/tabMove';
import {
  applyPinnedCount,
  moveTabBy,
  moveTabTo,
  pinnedCount,
  setTabPinned,
  tabShortcutFromEvent,
  type TabDragPayload,
} from '@/lib/tabStrip';
import { config } from '@/src/config';
import { PagePickerItems, type PageOption } from './PagePickerMenu';
import { TabStrip, type StripTab } from './TabStrip';
import { frameRef, useFrameBridge } from './useFrameBridge';
import { useHostAnnouncement, useTabMove, type MoveInResult } from './useTabMove';

export type { PageOption };

export interface AppTabsHostProps {
  /** Starting state; defaults to the current URL. */
  initialState?: AppPopoutState;
  /** Defaults to config.isDesktop. */
  isDesktop?: boolean;
  /** Pages the "+" menu offers — already filtered to what the user may open. */
  pages: PageOption[];
  /** Display name for a tab that has not reported a title of its own. */
  labelFor: (spec: AppTabSpec) => string;
  /** Told which page is in front (File ▸ New Window opens it). */
  onActiveChange?: (spec: AppTabSpec | null) => void;
  /** Overridable for tests. */
  hostId?: string;
}

interface OpenTab {
  /** Stable for the life of the tab, so a page that navigates keeps its iframe. */
  uid: string;
  spec: AppTabSpec;
  /** What the iframe was opened at. Never changes (changing it would reload the page). */
  src: string;
  /** Set by the page itself (e.g. a channel's name). */
  title?: string;
  /** False until the tab is first shown — background tabs cost nothing until then. */
  mounted: boolean;
  /** Pinned tabs sit left of the others, drawn compact (URL `pinned=` count). */
  pinned: boolean;
}

let uidSeq = 0;
const nextUid = () => `t${++uidSeq}`;

function makeTab(spec: AppTabSpec, isDesktop: boolean, mounted: boolean, title?: string): OpenTab {
  return { uid: nextUid(), spec, src: buildPagePopoutUrl(spec, { desktop: isDesktop }), mounted, pinned: false, ...(title ? { title } : {}) };
}

function initialTabs(state: AppPopoutState, isDesktop: boolean): { tabs: OpenTab[]; active: string | null } {
  const tabs = applyPinnedCount(
    state.tabs.map((spec) => makeTab(spec, isDesktop, appTabKey(spec) === state.active)),
    state.pinned ?? 0,
  );
  const active = tabs.find((t) => appTabKey(t.spec) === state.active) ?? tabs[0];
  if (active) active.mounted = true;
  return { tabs, active: active?.uid ?? null };
}

/** Rewrite the URL in place, keeping whichever form (path or ?popout=) we were opened with. */
function syncUrl(tabs: OpenTab[], activeUid: string | null) {
  if (typeof window === 'undefined' || !tabs.length) return;
  const active = tabs.find((t) => t.uid === activeUid);
  const state: AppPopoutState = {
    tabs: tabs.map((t) => t.spec),
    active: active ? appTabKey(active.spec) : null,
    pinned: pinnedCount(tabs),
  };
  const isPathForm = window.location.pathname.startsWith(APP_POPOUT_PATH);
  const next = `${window.location.pathname}?${buildAppPopoutQuery(state, { withFlag: !isPathForm })}`;
  if (`${window.location.pathname}${window.location.search}` !== next) {
    window.history.replaceState(window.history.state, '', next);
  }
}

export function AppTabsHost({
  initialState,
  isDesktop = config.isDesktop,
  pages,
  labelFor,
  onActiveChange,
  hostId: hostIdProp,
}: AppTabsHostProps) {
  const { t } = useTranslation();
  const [{ tabs, active }, setStrip] = useState(() =>
    initialTabs(
      initialState ?? parseAppPopoutParams(typeof window === 'undefined' ? '' : window.location.search),
      isDesktop,
    ),
  );
  const [popoutError, setPopoutError] = useState<{ message: string; retry?: () => void } | null>(null);
  const frames = useRef(new Map<string, HTMLIFrameElement>());
  const hostId = useMemo(() => hostIdProp ?? `h${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`, [hostIdProp]);

  const tabLabel = useCallback((tab: OpenTab) => tab.title || labelFor(tab.spec), [labelFor]);

  // ------------------------------------------------------------ state changes
  const activate = useCallback((uid: string) => {
    setStrip((s) =>
      s.tabs.some((x) => x.uid === uid)
        ? { tabs: s.tabs.map((x) => (x.uid === uid && !x.mounted ? { ...x, mounted: true } : x)), active: uid }
        : s,
    );
  }, []);

  const addTabs = useCallback(
    (incoming: AppPopoutState) => {
      setStrip((s) => {
        const next = [...s.tabs];
        let activeUid = s.active;
        for (const spec of incoming.tabs) {
          const key = appTabKey(spec);
          // Already-open pages are focused rather than duplicated.
          const existing = next.find((x) => appTabKey(x.spec) === key);
          if (existing) {
            if (key === incoming.active || incoming.tabs.length === 1) activeUid = existing.uid;
            continue;
          }
          if (next.length >= MAX_POPOUT_TABS) break;
          const tab = makeTab(spec, isDesktop, false);
          next.push(tab);
          if (key === incoming.active || incoming.tabs.length === 1) activeUid = tab.uid;
        }
        return {
          tabs: next.map((x) => (x.uid === activeUid && !x.mounted ? { ...x, mounted: true } : x)),
          active: activeUid ?? next[0]?.uid ?? null,
        };
      });
    },
    [isDesktop],
  );

  const closeTab = useCallback((uid: string) => {
    frames.current.delete(uid);
    setStrip((s) => {
      const idx = s.tabs.findIndex((x) => x.uid === uid);
      if (idx < 0) return s;
      const rest = s.tabs.filter((_, i) => i !== idx);
      let nextActive = s.active;
      if (s.active === uid) {
        const neighbour = rest[idx] ?? rest[idx - 1];
        nextActive = neighbour ? neighbour.uid : null;
      }
      return {
        tabs: rest.map((x) => (x.uid === nextActive && !x.mounted ? { ...x, mounted: true } : x)),
        active: nextActive,
      };
    });
  }, []);

  const reorder = useCallback((fn: (list: OpenTab[]) => OpenTab[]) => {
    setStrip((s) => {
      const next = fn(s.tabs);
      return next === s.tabs ? s : { ...s, tabs: next };
    });
  }, []);
  const moveTo = useCallback((uid: string, index: number) => reorder((list) => moveTabTo(list, uid, index)), [reorder]);
  const setPinned = useCallback((uid: string, pinned: boolean) => reorder((list) => setTabPinned(list, uid, pinned)), [reorder]);

  const closeOthers = useCallback((uid: string) => {
    setStrip((s) => {
      // Like a browser: pinned tabs survive "close other tabs".
      const keep = s.tabs.filter((x) => x.uid === uid || x.pinned);
      for (const x of s.tabs) if (!keep.includes(x)) frames.current.delete(x.uid);
      const nextActive = keep.some((x) => x.uid === uid) ? uid : keep[0]?.uid ?? null;
      return {
        tabs: keep.map((x) => (x.uid === nextActive && !x.mounted ? { ...x, mounted: true } : x)),
        active: nextActive,
      };
    });
  }, []);

  /** A page navigated inside its tab, or named itself. */
  const updateTab = useCallback((uid: string, key: string, title?: string) => {
    const spec = parseAppTabKey(key);
    if (!spec) return;
    setStrip((s) => ({
      ...s,
      tabs: s.tabs.map((x) =>
        x.uid === uid && (appTabKey(x.spec) !== key || x.title !== title) ? { ...x, spec, title } : x,
      ),
    }));
  }, []);

  const stripRef = useRef({ tabs, active });
  stripRef.current = { tabs, active };

  /** Open a tab in its own new window and remove it here. */
  const popOut = useCallback(
    async (uid: string, allowRetry = true): Promise<OpenResult> => {
      const tab = stripRef.current.tabs.find((x) => x.uid === uid);
      if (!tab) return { ok: false, error: 'no such tab' };
      setPopoutError(null);
      const result = await openAppPopout({ tabs: [tab.spec] }, 'window', { isDesktop, hostWindow: null });
      if (result.ok) closeTab(tab.uid);
      else {
        const blocked = result.error === 'popup blocked';
        setPopoutError({
          message: blocked
            ? t('appPopout.popup_blocked', 'Your browser blocked the new window. Allow pop-ups for this site and try again.')
            : t('appPopout.open_failed', 'Could not open a new window.'),
          // A click is a user gesture, so the popup blocker lets the retry through.
          retry: blocked && allowRetry ? () => void popOut(uid, false) : undefined,
        });
      }
      return result;
    },
    [closeTab, isDesktop, t],
  );

  // ------------------------------------------------------------ other windows
  const windowId = useMemo(() => (isDesktop ? currentDesktopWindowLabel() ?? hostId : hostId), [isDesktop, hostId]);

  const insertFromWindow = useCallback(
    (spec: AppTabSpec, index: number | undefined, title: string | undefined): MoveInResult => {
      const key = appTabKey(spec);
      const list = stripRef.current.tabs;
      const existing = list.find((x) => appTabKey(x.spec) === key);
      if (existing) {
        // This window already shows that page: the moved tab merges into it.
        activate(existing.uid);
        return 'ok';
      }
      if (list.length >= MAX_POPOUT_TABS) return 'full';
      const tab = makeTab(spec, isDesktop, true, title);
      setStrip((s) => ({ tabs: moveTabTo([...s.tabs, tab], tab.uid, index ?? s.tabs.length), active: tab.uid }));
      return 'ok';
    },
    [activate, isDesktop],
  );

  const movedOut = useCallback(
    (uid: string) => {
      const last = stripRef.current.tabs.length === 1 && stripRef.current.tabs[0]?.uid === uid;
      closeTab(uid);
      // A browser lets script close a window script opened; otherwise the empty state shows.
      if (last && !isDesktop) {
        try {
          window.close();
        } catch {
          /* ignore */
        }
      }
    },
    [closeTab, isDesktop],
  );

  const move = useTabMove({ windowId, enabled: true, isDesktop, onMoveIn: insertFromWindow, onMovedOut: movedOut });

  const moveToTarget = useCallback(
    (uid: string, target: MoveTarget) => {
      const tab = stripRef.current.tabs.find((x) => x.uid === uid);
      if (tab) void move.moveToWindow(uid, tab.spec, tab.title, target.id);
    },
    [move],
  );

  const startDrag = useCallback(
    (uid: string): TabDragPayload | null => {
      const tab = stripRef.current.tabs.find((x) => x.uid === uid);
      return tab ? move.startDrag(uid, tab.spec, tab.title) : null;
    },
    [move],
  );

  const dropForeign = useCallback(
    (payload: TabDragPayload, index: number) => {
      if (move.acceptDrop(payload, index) === 'full') {
        setPopoutError({ message: t('tabStrip.window_full', 'This window has as many tabs as it can hold. Close one first.') });
      }
    },
    [move, t],
  );

  const dragOut = useCallback(
    (uid: string, nonce: string | null) => {
      // A lone tab torn off would just be this window again.
      if (stripRef.current.tabs.length < 2) return;
      // Let a late ack from a window that did take the drop arrive first.
      window.setTimeout(() => {
        if (nonce && !move.claimDragOut(nonce)) return;
        void popOut(uid);
      }, 150);
    },
    [move, popOut],
  );

  const handleShortcut = useCallback(
    (key: string) => {
      const { tabs: list, active: cur } = stripRef.current;
      if (/^[1-9]$/.test(key)) {
        const n = Number(key);
        const target = n === 9 ? list[list.length - 1] : list[n - 1];
        if (target) activate(target.uid);
        return true;
      }
      if (key === 'w' && isDesktop && cur) {
        closeTab(cur);
        return true;
      }
      if ((key === 'move-left' || key === 'move-right') && cur) {
        reorder((l) => moveTabBy(l, cur, key === 'move-left' ? -1 : 1));
        return true;
      }
      return false;
    },
    [activate, closeTab, isDesktop, reorder],
  );

  // ------------------------------------------------------------ side effects
  useEffect(() => {
    syncUrl(tabs, active);
  }, [tabs, active]);

  // When the last tab closes, a desktop window closes itself. A browser only
  // lets script close a window script opened, so there we show the empty state.
  useEffect(() => {
    if (tabs.length || !isDesktop) return;
    import('@tauri-apps/api/window')
      .then(({ getCurrentWindow }) => getCurrentWindow().close())
      .catch(() => {});
  }, [tabs.length, isDesktop]);

  const activeTab = tabs.find((x) => x.uid === active) ?? null;
  const activeSpec = activeTab?.spec ?? null;
  useEffect(() => {
    onActiveChange?.(activeSpec);
  }, [activeSpec, onActiveChange]);
  const title = activeTab ? `${tabLabel(activeTab)} · WeaveIn` : 'WeaveIn';
  useEffect(() => {
    document.title = title;
    if (!isDesktop) return;
    import('@tauri-apps/api/window')
      .then(({ getCurrentWindow }) => getCurrentWindow().setTitle(title))
      .catch(() => {});
  }, [title, isDesktop]);

  // "Open in new tab" from this document adds a tab here.
  useEffect(() => registerInAppTabHandler(addTabs), [addTabs]);

  // Pages in our iframes, and the window that opened us, talk to us here.
  useFrameBridge(
    frames,
    (uid, msg) => {
      if (msg.type === 'weavein:page-state') updateTab(uid, msg.key, msg.title);
      else if (msg.type === 'weavein:open') addTabs(stateFromKeys(msg.tabs, msg.active));
      else if (msg.type === 'weavein:shortcut') handleShortcut(msg.key);
    },
    (e) => {
      const d = e.data as { type?: unknown; tabs?: unknown; active?: unknown } | null;
      if (!d || d.type !== 'weavein:add-tabs') return;
      const state = stateFromKeys(d.tabs, d.active);
      if (state.tabs.length) addTabs(state);
    },
  );

  // Web: announce this host so "Add to tab view" and "Move to ▸" in any other window can find it.
  useHostAnnouncement(hostId, { enabled: !isDesktop, kind: 'tabs', title });
  useEffect(() => {
    if (isDesktop) return;
    let channel: BroadcastChannel | null = null;
    try {
      channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(HOST_CHANNEL);
    } catch {
      channel = null;
    }
    if (!channel) return;
    channel.onmessage = (e: MessageEvent) => {
      const d = e.data as { type?: unknown; target?: unknown; tabs?: unknown; active?: unknown } | null;
      if (!d || d.type !== 'weavein:add-tabs' || d.target !== hostId) return;
      const state = stateFromKeys(d.tabs, d.active);
      if (!state.tabs.length) return;
      addTabs(state);
      // Browsers usually ignore this when the request came from another
      // window, but it is the best a page can do to come forward.
      window.focus();
    };
    return () => channel?.close();
  }, [isDesktop, hostId, addTabs]);

  // Desktop: the main window's "Add to tab view" targets this window by label.
  useEffect(() => {
    if (!isDesktop) return;
    const label = currentDesktopWindowLabel();
    const remember = () => {
      try {
        if (label) window.localStorage.setItem(LAST_DESKTOP_HOST_KEY, label);
      } catch {
        /* ignore */
      }
    };
    remember();
    window.addEventListener('focus', remember);
    let unlisten: (() => void) | undefined;
    let cancelled = false;
    import('@tauri-apps/api/webviewWindow')
      .then(({ getCurrentWebviewWindow }) =>
        getCurrentWebviewWindow().listen<AddTabsEvent>(ADD_TABS_EVENT, (e) => {
          if (!e.payload || e.payload.target !== label) return;
          const state = stateFromKeys(e.payload.tabs, e.payload.active);
          if (state.tabs.length) addTabs(state);
        }),
      )
      .then((fn) => {
        if (cancelled) fn();
        else unlisten = fn;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      window.removeEventListener('focus', remember);
      unlisten?.();
    };
  }, [isDesktop, addTabs]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const key = tabShortcutFromEvent(e);
      if (key && handleShortcut(key)) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleShortcut]);

  const openKeys = new Set(tabs.map((x) => appTabKey(x.spec)));
  const stripTabs: StripTab[] = tabs.map((tab) => {
    const key = appTabKey(tab.spec);
    return {
      uid: tab.uid,
      key,
      label: tabLabel(tab),
      pinned: tab.pinned,
      closable: true,
      movable: true,
      domId: `popout-tab-${tab.uid}`,
      panelId: `popout-panel-${tab.uid}`,
      testId: `popout-tab-${key}`,
      closeTestId: `popout-close-${key}`,
      menuTestId: `popout-menu-${key}`,
    };
  });

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-white dark:bg-slate-950">
      <TabStrip
        tabs={stripTabs}
        activeUid={active}
        ariaLabel={t('appPopout.tabs_label', 'Open pages')}
        testIdPrefix="popout-tab"
        isDesktop={isDesktop}
        variant="popout"
        className="flex flex-shrink-0 items-end gap-1 border-b bg-slate-100 px-2 pt-1.5 dark:bg-slate-900"
        onActivate={activate}
        onClose={closeTab}
        onMoveTo={moveTo}
        onPin={setPinned}
        onCloseOthers={closeOthers}
        onMoveToNewWindow={(uid) => void popOut(uid)}
        onMoveToTarget={moveToTarget}
        listTargets={move.listTargets}
        startDrag={startDrag}
        onDropForeign={dropForeign}
        onDragOut={dragOut}
        tabExtras={(tab) =>
          tabs.length > 1 ? (
            <button
              type="button"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                void popOut(tab.uid);
              }}
              className="rounded p-0.5 opacity-0 hover:bg-slate-300 group-hover:opacity-100 focus:opacity-100 dark:hover:bg-slate-700"
              aria-label={t('appPopout.pop_out_tab', 'Pop out into its own window')}
              title={t('appPopout.pop_out_tab', 'Pop out into its own window')}
              data-testid={`popout-popout-${tab.key}`}
            >
              <AppWindow className="h-3 w-3" />
            </button>
          ) : null
        }
        addControl={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="mb-1 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-800"
                aria-label={t('appPopout.add_tab', 'Open a page in a new tab')}
                title={t('appPopout.add_tab', 'Open a page in a new tab')}
                data-testid="popout-add-tab"
              >
                <Plus className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[70vh] w-64 overflow-y-auto">
              <PagePickerItems
                pages={pages}
                openKeys={openKeys}
                onPick={(p) => addTabs({ tabs: [{ tab: p.tab }], active: p.tab })}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      {popoutError && (
        <div
          role="alert"
          className="flex flex-shrink-0 items-center gap-3 bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
        >
          <span className="flex-1">{popoutError.message}</span>
          {popoutError.retry && (
            <button
              type="button"
              onClick={popoutError.retry}
              className="rounded border border-red-300 px-2 py-0.5 font-medium hover:bg-red-100 dark:border-red-700 dark:hover:bg-red-900/40"
              data-testid="popout-retry-open"
            >
              {t('tabStrip.open_anyway', 'Open')}
            </button>
          )}
          <button
            type="button"
            onClick={() => setPopoutError(null)}
            className="rounded px-1 hover:bg-red-100 dark:hover:bg-red-900/40"
            aria-label={t('tabStrip.dismiss', 'Dismiss')}
          >
            ×
          </button>
        </div>
      )}

      {/* Panels — mounted on first view, then kept mounted and hidden */}
      <div className="relative min-h-0 flex-1">
        {tabs.map((tab) => {
          const isActive = tab.uid === active;
          const key = appTabKey(tab.spec);
          return (
            <div
              key={tab.uid}
              role="tabpanel"
              id={`popout-panel-${tab.uid}`}
              aria-labelledby={`popout-tab-${tab.uid}`}
              data-testid={`popout-panel-${key}`}
              className={isActive ? 'h-full min-h-0 overflow-hidden' : 'hidden'}
            >
              {tab.mounted && (
                <iframe
                  ref={frameRef(frames, tab.uid)}
                  src={tab.src}
                  // Same-origin page frames may run an in-app call (TRUS-668).
                  allow="camera; microphone; autoplay; display-capture"
                  title={tabLabel(tab)}
                  className="h-full w-full border-0"
                  data-testid={`popout-frame-${key}`}
                />
              )}
            </div>
          );
        })}
        {!tabs.length && (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-sm text-muted-foreground">
            <p>{t('appPopout.no_tabs', 'No tabs open.')}</p>
            <div className="flex max-w-2xl flex-wrap justify-center gap-2">
              {pages.slice(0, 12).map((p) => (
                <button
                  key={p.tab}
                  type="button"
                  onClick={() => addTabs({ tabs: [{ tab: p.tab }], active: p.tab })}
                  className="rounded-md border px-3 py-1.5 text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-900"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AppTabsHost;
